/**
 * ProShip Direct v2 — Courier Rules.
 *
 * A common panel feature, not a Direct one: a merchant with no Direct couriers still wants to say
 * which ProShip courier wins which lane. The panel's existing /merchant-rules is a read-only
 * viewer whose empty state tells the merchant to call their account manager; this is the version
 * they can drive themselves.
 *
 * Rules are append-only. They can be created, cloned, reordered and switched off — never edited
 * or deleted. A rule that has allocated orders is the record of why those orders went where they
 * did; editing it would rewrite that history and deleting it would erase it. Changing your mind
 * means switching one off and cloning a replacement, which keeps the audit whole.
 *
 * Order matters: the first rule whose conditions match decides the allocation, so the list is
 * reorderable and the broadest rule belongs last.
 */
import React, { useMemo, useState } from 'react';
import {
  Box, Drawer, Typography, Button, Switch, TextField, InputAdornment, IconButton, Tooltip, ToggleButton,
  ToggleButtonGroup, Table, TableBody, TableCell, TableHead, TableRow, Chip, Menu, MenuItem, Alert,
  ListItemIcon, ListItemText,
} from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import CloseIcon from '@mui/icons-material/Close';

import withLayout from '../../../hocs/Layout';
import PageTitle from '../../../common/PageTitle';
import AllocationSimulator from '../components/AllocationSimulator';
import { pageContainerSx, sectionCardSx, primaryBtnSx, secondaryBtnSx } from '../../../styles/patterns';
import { SUBTLE, NAVY, BORDER, WHITE, LIGHT_BG, PRIMARY_BLUE, PURPLE, SUCCESS } from '../../../styles/foundation/colors';
import { FONT, SIZE, WEIGHT } from '../../../styles/foundation/typography';
import { GAP, RADIUS } from '../../../styles/foundation/spacing';
import { formatDate } from '../../../helpers/UtilityHelper';
import { tHeadSx, tCellSx, statLabelSx } from '../components/stats';

import { useShippingRules, useCourierPool, usePricingModel, ACTIVITY_ENTITY } from '../data/hooks';
import BulkRuleUploadModal from '../components/BulkRuleUploadModal';
import { rulesToCsv, RULE_CSV_TEMPLATE } from '../data/ruleCsv';
import { downloadCsv } from '../data/serviceabilityUpload';
import ActivityLogModal from '../components/ActivityLogModal';
import { PRIORITY_STRATEGIES, RULE_STATUS_TABS, RULE_CONDITION_TYPES } from '../data/defaults';
import { SETTINGS_PATHS } from '../flags';

const subSx = { fontFamily: FONT, fontSize: SIZE['3xs'], color: SUBTLE };
const nameSx = { fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold, color: NAVY };

const CONTROL_HEIGHT = 34;
const menuItemSx = { fontFamily: FONT, fontSize: SIZE['2xs'], py: 0.75 };
const fieldSx = {
  '& .MuiInputBase-root': { fontFamily: FONT, fontSize: SIZE['2xs'], height: CONTROL_HEIGHT },
};

const tabSx = {
  fontFamily: FONT,
  fontSize: SIZE['2xs'],
  fontWeight: WEIGHT.semibold,
  textTransform: 'none',
  py: 0.4,
  px: 1.75,
  border: `1px solid ${BORDER}`,
  color: SUBTLE,
  '&.Mui-selected': { bgcolor: `${PRIMARY_BLUE}14`, color: PRIMARY_BLUE, borderColor: PRIMARY_BLUE },
};

const strategyChipSx = {
  fontFamily: FONT,
  fontSize: SIZE['4xs'],
  fontWeight: WEIGHT.semibold,
  height: 19,
  borderRadius: '4px',
  bgcolor: `${PURPLE}14`,
  color: PURPLE,
};

/** One line summarising what a rule matches, so the table says something without expanding. */
const summarise = (rule) => {
  if (!rule.conditions?.length) return 'Matches every order';
  return rule.conditions
    .map((c) => {
      const type = RULE_CONDITION_TYPES.find((t) => t.key === c.type);
      const label = type?.label || c.type;
      /*
      A time window's value is an object, and it must be named before the generic fallbacks —
      an object is not an array and has no operator, so it reached the last line and printed as
      "[object Object]". Same bug the preview panel had.
      */
      if (type?.kind === 'between') {
        const { from, to } = c.value || {};
        if (!from || !to) return `${label}: window incomplete`;
        return `${label}: ${from}–${to}${to < from ? ' (next day)' : ''}`;
      }
      if (Array.isArray(c.value)) return `${label}: ${c.value.join(', ')}`;
      if (c.operator) return `${label} ${c.operator === 'GTE' ? '≥' : c.operator === 'LTE' ? '≤' : c.operator === 'GT' ? '>' : c.operator === 'LT' ? '<' : '='} ${c.value}`;
      return `${label}: ${c.value}`;
    })
    .join(' · ');
};

function ShippingRules() {
  const navigate = useNavigate();
  const { rules, createRule, setStatus, reorderRules } = useShippingRules();
  const { pool } = useCourierPool();
  const { isBlended } = usePricingModel();
  const [tab, setTab] = useState('ACTIVE');
  const [query, setQuery] = useState('');
  const [bulkOpen, setBulkOpen] = useState(false);
  const [logOpen, setLogOpen] = useState(false);
  const [simOpen, setSimOpen] = useState(false);
  const [dragId, setDragId] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rules.filter((r) => {
      if (tab === 'ACTIVE' && !r.enabled) return false;
      if (tab === 'INACTIVE' && r.enabled) return false;
      return !q || r.name.toLowerCase().includes(q);
    });
  }, [rules, tab, query]);

  const strategyLabel = (key) => PRIORITY_STRATEGIES.find((s) => s.key === key)?.label || key;

  /**
   * Move a rule past its neighbour *in the visible list*, not in the full one.
   *
   * Swapping with the adjacent rule in `rules` looks like nothing happened whenever a filter or
   * the Active/Inactive tab hides the rule in between — the rule really moved, past something the
   * merchant cannot see. Moving past the next visible rule is what they asked for, and it still
   * writes a real position into the full evaluation order.
   */
  const moveVisible = (id, direction) => {
    const visibleIds = visible.map((r) => r.id);
    const from = visibleIds.indexOf(id);
    const targetId = visibleIds[from + direction];
    if (!targetId) return;

    const ids = rules.map((r) => r.id);
    const a = ids.indexOf(id);
    const b = ids.indexOf(targetId);
    [ids[a], ids[b]] = [ids[b], ids[a]];
    reorderRules(ids);
  };

  return (
    <Box sx={pageContainerSx}>
      <PageTitle subTitle='The first rule whose conditions match decides how the courier is chosen'>
        Courier Rules
      </PageTitle>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: GAP.md }}>
        {/*
          Blended pricing means ProShip quotes one rate across couriers, so which courier carries
          an order is ours to decide — authoring rules would imply a control the merchant does not
          have. Enable and disable stay available: switching a rule off is a request to stop
          applying it, which is meaningful either way.
        */}
        {isBlended && (
          <Alert severity='info' sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], py: 0.25 }}>
            <strong>You are on blended pricing.</strong> Courier rules are view-only — ProShip
            chooses the courier within your blended rate. You can still switch a rule on or off, and
            you can see exactly what each one does. Talk to your account manager to move to
            courier-wise pricing.
          </Alert>
        )}

        {/* Never wraps — scrolls sideways at narrow widths so the primary action stays in place. */}
        <Box sx={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center', gap: GAP.sm, overflowX: 'auto', pb: 0.25 }}>
          <TextField
            size='small'
            placeholder='Search rule name'
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            sx={{ ...fieldSx, width: 210, flexShrink: 0 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchOutlinedIcon sx={{ fontSize: 15, color: SUBTLE }} />
                </InputAdornment>
              ),
            }}
          />
          <ToggleButtonGroup
            exclusive
            size='small'
            value={tab}
            onChange={(_e, v) => v && setTab(v)}
            sx={{ height: CONTROL_HEIGHT, flexShrink: 0 }}
          >
            {RULE_STATUS_TABS.map((t) => (
              <ToggleButton key={t.key} value={t.key} sx={tabSx}>
                {t.label}
                {t.key === 'ACTIVE' && ` (${rules.filter((r) => r.enabled).length})`}
                {t.key === 'INACTIVE' && ` (${rules.filter((r) => !r.enabled).length})`}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          <Box sx={{ flex: 1 }} />

          {/*
            Four buttons wrapped onto a second line and pushed the primary action below the filters.
            The panel's convention for several actions on one screen is a MoreVert menu (see the
            orders list), so the three secondary ones move there and Create New Rule is left as the
            only button competing for attention. The row also no longer wraps — it scrolls.

            Download does two jobs: export what exists, or hand over the template when there is
            nothing to export, since an empty CSV of headers would be a dead end.
          */}
          <Button
            size='small'
            onClick={() => setSimOpen(true)}
            startIcon={<ScienceOutlinedIcon sx={{ fontSize: 16 }} />}
            sx={{ ...secondaryBtnSx, height: CONTROL_HEIGHT, flexShrink: 0 }}
          >
            Simulate an order
          </Button>

          <Tooltip title='More actions'>
            <IconButton
              size='small'
              onClick={(e) => setMenuAnchor(e.currentTarget)}
              aria-label='More rule actions'
              sx={{
                border: `1px solid ${BORDER}`,
                borderRadius: RADIUS.sm,
                height: CONTROL_HEIGHT,
                width: CONTROL_HEIGHT,
                flexShrink: 0,
              }}
            >
              <MoreVertIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
            {/* Export stays available on blended — reading your rules is not authoring them. */}
            <MenuItem
              sx={menuItemSx}
              onClick={() => {
                setMenuAnchor(null);
                if (rules.length) downloadCsv('courier-rules.csv', rulesToCsv(rules));
                else downloadCsv('courier-rules-template.csv', RULE_CSV_TEMPLATE);
              }}
            >
              <ListItemIcon sx={{ minWidth: 28 }}><DownloadOutlinedIcon sx={{ fontSize: 16 }} /></ListItemIcon>
              <ListItemText primaryTypographyProps={{ sx: menuItemSx }}>
                {rules.length ? `Download ${rules.length} rules as CSV` : 'Download CSV template'}
              </ListItemText>
            </MenuItem>
            {!isBlended && (
              <MenuItem sx={menuItemSx} onClick={() => { setMenuAnchor(null); setBulkOpen(true); }}>
                <ListItemIcon sx={{ minWidth: 28 }}><UploadFileOutlinedIcon sx={{ fontSize: 16 }} /></ListItemIcon>
                <ListItemText primaryTypographyProps={{ sx: menuItemSx }}>Bulk upload from CSV</ListItemText>
              </MenuItem>
            )}
            <MenuItem sx={menuItemSx} onClick={() => { setMenuAnchor(null); setLogOpen(true); }}>
              <ListItemIcon sx={{ minWidth: 28 }}><HistoryOutlinedIcon sx={{ fontSize: 16 }} /></ListItemIcon>
              <ListItemText primaryTypographyProps={{ sx: menuItemSx }}>Activity log</ListItemText>
            </MenuItem>
          </Menu>

          {!isBlended && (
            <Button
              component={Link}
              to={SETTINGS_PATHS.ruleBuilder}
              variant='contained'
              size='small'
              startIcon={<AddOutlinedIcon sx={{ fontSize: 16 }} />}
              sx={{ ...primaryBtnSx, height: CONTROL_HEIGHT, flexShrink: 0 }}
            >
              Create New Rule
            </Button>
          )}
        </Box>

        {visible.length === 0 ? (
          <Box sx={{ ...sectionCardSx, textAlign: 'center', py: 4 }}>
            <Typography sx={{ ...nameSx, mb: 0.5 }}>
              {rules.length === 0 ? 'No rules yet' : `No ${tab.toLowerCase()} rule matches`}
            </Typography>
            <Typography sx={subSx}>
              {rules.length === 0 ? (
                <>
                  With no rules, PACE allocates every order across whatever is enabled.{' '}
                  <Box component={Link} to={SETTINGS_PATHS.ruleBuilder} sx={{ color: PRIMARY_BLUE }}>
                    Create a rule
                  </Box>{' '}
                  only where you want to override that.
                </>
              ) : (
                'Try another tab or clear the search.'
              )}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: RADIUS.md, overflowX: 'auto', bgcolor: WHITE }}>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...tHeadSx, width: 78 }}>Order</TableCell>
                  <TableCell sx={{ ...tHeadSx, minWidth: 240 }}>Rule Name</TableCell>
                  <TableCell sx={{ ...tHeadSx, minWidth: 110 }}>Allocation</TableCell>
                  <TableCell sx={{ ...tHeadSx, minWidth: 160 }}>Created By</TableCell>
                  <TableCell sx={{ ...tHeadSx, minWidth: 130 }}>Created On</TableCell>
                  <TableCell sx={{ ...tHeadSx, minWidth: 110 }}>Status</TableCell>
                  <TableCell sx={{ ...tHeadSx, minWidth: 110 }}>
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4 }}>
                      Fallback
                      <Tooltip
                        title={
                          <Box sx={{ py: 0.5 }}>
                            <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold, mb: 0.75 }}>
                              Fallback in courier rule engine
                            </Typography>
                            <Box component='ul' sx={{ m: 0, pl: 2, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              <Box component='li' sx={{ fontFamily: FONT, fontSize: SIZE['3xs'], lineHeight: 1.5 }}>
                                <strong>Enabled:</strong> if the current rule fails to allocate a
                                courier, the engine tries the next eligible rule.
                              </Box>
                              <Box component='li' sx={{ fontFamily: FONT, fontSize: SIZE['3xs'], lineHeight: 1.5 }}>
                                <strong>Disabled:</strong> if the current rule fails, an error is
                                returned without checking other rules.
                              </Box>
                            </Box>
                          </Box>
                        }
                      >
                        <InfoOutlinedIcon sx={{ fontSize: 13 }} />
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ ...tHeadSx, minWidth: 130 }}>Last Modified</TableCell>
                  <TableCell sx={{ ...tHeadSx, minWidth: 90 }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visible.map((rule) => {
                  // Sequential within what is on screen. On the Active tab a merchant reads this
                  // as "the order these rules run in", so a number that skips the disabled rules
                  // sitting between them raises a question the column cannot answer.
                  const index = visible.findIndex((r) => r.id === rule.id);
                  return (
                    <TableRow
                      key={rule.id}
                      hover
                      draggable
                      onDragStart={() => setDragId(rule.id)}
                      onDragEnd={() => setDragId(null)}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={() => {
                        if (dragId && dragId !== rule.id) {
                          const ids = rules.map((r) => r.id);
                          const from = ids.indexOf(dragId);
                          const to = ids.indexOf(rule.id);
                          ids.splice(to, 0, ids.splice(from, 1)[0]);
                          reorderRules(ids);
                        }
                        setDragId(null);
                      }}
                      sx={{
                        opacity: rule.enabled ? (dragId === rule.id ? 0.4 : 1) : 0.62,
                        cursor: 'grab',
                      }}
                    >
                      <TableCell sx={tCellSx}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <DragIndicatorIcon sx={{ fontSize: 15, color: SUBTLE }} />
                          <Typography sx={{ ...statLabelSx, width: 16 }}>{index + 1}</Typography>
                          <Box>
                            <IconButton size='small' sx={{ p: 0.1 }} disabled={visible[0]?.id === rule.id} onClick={() => moveVisible(rule.id, -1)} aria-label='Move rule up'>
                              <ArrowUpwardIcon sx={{ fontSize: 13 }} />
                            </IconButton>
                            <IconButton size='small' sx={{ p: 0.1 }} disabled={visible[visible.length - 1]?.id === rule.id} onClick={() => moveVisible(rule.id, 1)} aria-label='Move rule down'>
                              <ArrowDownwardIcon sx={{ fontSize: 13 }} />
                            </IconButton>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ ...tCellSx, whiteSpace: 'normal', minWidth: 240 }}>
                        <Typography sx={nameSx}>{rule.name}</Typography>
                        <Typography sx={subSx}>{summarise(rule)}</Typography>
                      </TableCell>
                      <TableCell sx={tCellSx}>
                        <Chip size='small' label={strategyLabel(rule.strategy)} sx={strategyChipSx} />
                      </TableCell>
                      <TableCell sx={{ ...tCellSx, color: SUBTLE }}>{rule.createdBy}</TableCell>
                      <TableCell sx={{ ...tCellSx, color: SUBTLE }}>
                        {formatDate(rule.createdOn, 'dd MMM, HH:mm')}
                      </TableCell>
                      <TableCell sx={tCellSx}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                          <Switch
                            size='small'
                            checked={rule.enabled}
                            onChange={() => setStatus(rule.id, 'enabled', !rule.enabled)}
                          />
                          <Typography sx={{ ...subSx, color: rule.enabled ? SUCCESS : SUBTLE }}>
                            {rule.enabled ? 'Enabled' : 'Disabled'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={tCellSx}>
                        <Switch
                          size='small'
                          checked={Boolean(rule.fallback)}
                          onChange={() => setStatus(rule.id, 'fallback', !rule.fallback)}
                        />
                      </TableCell>
                      <TableCell sx={{ ...tCellSx, color: SUBTLE }}>
                        {formatDate(rule.lastModified, 'dd MMM, HH:mm')}
                      </TableCell>
                      <TableCell sx={tCellSx}>
                        <Tooltip title='View rule'>
                          <IconButton
                            size='small'
                            onClick={() => navigate(`${SETTINGS_PATHS.ruleBuilder}?view=${rule.id}`)}
                            aria-label={`View ${rule.name}`}
                          >
                            <VisibilityOutlinedIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                        {/* Clone creates a rule, so it goes with the other authoring controls. */}
                        {!isBlended && (
                          <Tooltip title='Clone into a new rule'>
                            <IconButton
                              size='small'
                              onClick={() => navigate(`${SETTINGS_PATHS.ruleBuilder}?clone=${rule.id}`)}
                              aria-label={`Clone ${rule.name}`}
                            >
                              <ContentCopyOutlinedIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: GAP.sm, alignItems: 'flex-start', p: 1.25, bgcolor: LIGHT_BG, borderRadius: RADIUS.md }}>
          <InfoOutlinedIcon sx={{ fontSize: 15, color: SUBTLE, mt: '1px' }} />
          <Typography sx={subSx}>
            Rules are evaluated top to bottom and the first match wins, so keep the broadest rule
            last. A rule cannot be edited or deleted once created — switch it off and clone a
            replacement, so the record of how past orders were allocated stays intact.
          </Typography>
        </Box>
      </Box>

      {/*
        A drawer rather than a dialog: the trace is one row per rule and grows with the rule set, so
        it needs height, and a merchant reading it wants the list still visible behind.
      */}
      <Drawer
        anchor='right'
        open={simOpen}
        onClose={() => setSimOpen(false)}
        PaperProps={{ sx: { width: { xs: '100%', md: 720 }, bgcolor: LIGHT_BG } }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: GAP.sm,
            px: 2,
            py: 1.5,
            bgcolor: WHITE,
            borderBottom: `1px solid ${BORDER}`,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontFamily: FONT, fontSize: SIZE.md, fontWeight: WEIGHT.bold, color: NAVY }}>
              Simulate an order
            </Typography>
            <Typography sx={{ fontFamily: FONT, fontSize: SIZE['3xs'], color: SUBTLE }}>
              Against all {rules.length} rules, in the order they run
            </Typography>
          </Box>
          <IconButton size='small' onClick={() => setSimOpen(false)} aria-label='Close simulator'>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
        <Box sx={{ p: 2, overflowY: 'auto' }}>
          <AllocationSimulator rules={rules} pool={pool} />
        </Box>
      </Drawer>

      {logOpen && (
        <ActivityLogModal
          entity={ACTIVITY_ENTITY.RULE}
          title='Rule activity'
          onClose={() => setLogOpen(false)}
        />
      )}

      {bulkOpen && (
        <BulkRuleUploadModal
          existingNames={rules.map((r) => r.name)}
          courierCodes={pool.map((p) => p.key.split(':')[1])}
          onCreate={createRule}
          onClose={() => setBulkOpen(false)}
        />
      )}
    </Box>
  );
}

export default withLayout(ShippingRules);
